import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

export interface PortalProps {
    children: React.ReactNode;
}

export const GDS_PORTAL_ID= 'portal-root';

const createPortalParent = (id: string): HTMLDivElement => {
    const el = document.createElement('div');
    el.setAttribute('id', id);
    el.style.position = 'absolute';
    el.style.left = '0';
    el.style.top = '0';
    el.style.width = '100%';

    return el;
};

const placePortalParent = (el: HTMLDivElement) => {
    document.body.appendChild(el);
};

const usePortal = (id = GDS_PORTAL_ID) => {
    const portalRootRef = useRef<HTMLDivElement | null>(null);

    useEffect(function setupPortal() {
        const existingParent = document.getElementById(id) as HTMLDivElement | null;
        const parent = existingParent || createPortalParent(id);

        if (!existingParent) {
            placePortalParent(parent);
        }

        if (portalRootRef.current) {
            parent.appendChild(portalRootRef.current);
        }

        return () => {
            if (portalRootRef.current) {
                parent.removeChild(portalRootRef.current);
            }
        };
    }, [ id ]);

    const getPortalRoot = () => {
        if (!portalRootRef.current) {
            portalRootRef.current = document.createElement('div');
        }

        return portalRootRef.current;
    };

    return getPortalRoot();
};

const Portal: React.FC<PortalProps> = ({ children }) => {
    const target = usePortal();

    return createPortal(children, target);
};

export default Portal;
